import React from "react";

import { Check, Info, Users } from "lucide-react";

import {
  getAssessmentSubmitSuccessCopy,
  type AssessmentSubmitSuccessInfo,
} from "./assessmentSubmitSuccessCopy";
import Button from "../../Components/Button";
import Modal from "../../Components/Modal";

import "./AssessmentSubmitSuccessModal.scss";

export type { AssessmentSubmitSuccessInfo } from "./assessmentSubmitSuccessCopy";

interface AssessmentSubmitSuccessModalProps {
  info: AssessmentSubmitSuccessInfo | null;
  onClose: () => void;
  onViewMix: (info: AssessmentSubmitSuccessInfo) => void;
  onBackToStatus?: () => void;
  showBackToStatus?: boolean;
}

const calloutToneClass: Record<"team" | "esl", string> = {
  team: "border-violet-200 bg-violet-50 text-violet-900",
  esl: "border-sky-200 bg-sky-50 text-sky-900",
};

/** v7 spec §5.1 — final (non-draft) Team, Formal, and Disconnect submit. */
const AssessmentSubmitSuccessModal: React.FC<
  AssessmentSubmitSuccessModalProps
> = ({ info, onClose, onViewMix, onBackToStatus, showBackToStatus = true }) => {
  if (!info) return null;

  const copy = getAssessmentSubmitSuccessCopy(info);
  const canGoToStatus = showBackToStatus && Boolean(onBackToStatus);
  const showMix = copy.showMixCta;
  const statusVariant = showMix ? "outline-primary" : "primary";

  return (
    <Modal isOpen={!!info} toggle={onClose} title={null} size="2xl">
      <div className="assessment-submit-success flex flex-col items-center text-center">
        <div className="assessment-submit-success__tick mb-4" aria-hidden>
          <span className="assessment-submit-success__tick-ring" />
          <span className="assessment-submit-success__tick-badge">
            <Check
              size={28}
              strokeWidth={3}
              className="assessment-submit-success__tick-icon"
            />
          </span>
        </div>
        <h2 className="mb-2 text-xl font-semibold text-gray-900">
          {copy.title}
        </h2>
        <p className="mb-0 max-w-xl text-sm text-gray-700">{copy.body}</p>
        <div
          className={`mt-4 flex w-full items-start gap-3 rounded-lg border px-3 py-3 text-left text-sm ${calloutToneClass[copy.callout.tone]}`}
        >
          {copy.callout.tone === "esl" ? (
            <Info size={18} className="mt-0.5 shrink-0" aria-hidden />
          ) : (
            <Users size={18} className="mt-0.5 shrink-0" aria-hidden />
          )}
          <p className="mb-0 whitespace-pre-line">{copy.callout.text}</p>
        </div>
        <div className="mt-6 flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-gray-500 hover:text-gray-700 sm:mr-auto"
          >
            Stay Here
          </button>
          {canGoToStatus && (
            <Button
              variant={statusVariant}
              type="button"
              onClick={onBackToStatus}
            >
              Back to Status
            </Button>
          )}
          {showMix && (
            <Button
              variant="primary"
              type="button"
              onClick={() => onViewMix(info)}
            >
              View Levels Mix
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AssessmentSubmitSuccessModal;
